import React from 'react';
import { TripInfo, ItineraryItem } from '../../types/Quotation';
import { Section } from './UIPrimitives';
import PDFPreviewModal from '../QuotationBuilder/PDFPreviewModal';
import PackageCard from './PackageCard';

type Addon = { name: string; price: number; currency: string; description: string };
type Review = { date: string; user: string; rating: number; comment: string };
type Faq = { question: string; answer: string };

export interface PackageRightSidebarProps {
  heroImage: string;
  tripInfo: TripInfo;
  pricePer: 'person' | 'package' | 'night';
  currency: 'INR' | 'USD';
  rating: string;
  strikePrice: number;
  slug: string;
  websitePreviewUrl: string;
  cities: string[];
  nightsCount: number;
  highlights: string[];
  exclusions: string[];
  addons: Addon[];
  reviews: Review[];
  faq: Faq[];
  itinerary: ItineraryItem[];
  onChangeTotalPrice: (value: number) => void;
}

const PackageRightSidebar: React.FC<PackageRightSidebarProps> = ({
  heroImage,
  tripInfo,
  pricePer,
  currency,
  rating,
  strikePrice,
  slug,
  websitePreviewUrl,
  cities,
  nightsCount,
  highlights,
  exclusions,
  addons,
  reviews,
  faq,
  itinerary,
  onChangeTotalPrice,
}) => {
  const flightsTotal = itinerary.reduce((sum, day) => sum + day.flights.reduce((daySum, flight) => daySum + (flight.price || 0), 0), 0);
  const hotelsTotal = itinerary.reduce((sum, day) => sum + day.hotels.reduce((daySum, hotel) => daySum + (hotel.price || 0), 0), 0);
  const activitiesTotal = itinerary.reduce((sum, day) => sum + day.activities.reduce((daySum, activity) => daySum + (activity.from_price_paise ? Math.round(activity.from_price_paise / 100) : 0), 0), 0);
  const transfersTotal = itinerary.reduce((sum, day) => sum + (day.transfers || []).reduce((daySum, t) => daySum + (t.price || 0), 0), 0);
  const addonsTotal = addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
  const autoTotal = flightsTotal + hotelsTotal + activitiesTotal + transfersTotal + addonsTotal;

  const currencySymbol = currency === 'USD' ? '$' : '₹';

  const [showPDF, setShowPDF] = React.useState(false);

  return (
    <div className="package-editor-preview-section">
      {showPDF && (
        <PDFPreviewModal
          tripInfo={tripInfo}
          itinerary={itinerary}
          highlights={highlights.join(', ')}
          onClose={() => setShowPDF(false)}
        />
      )}
      <Section title="Live Preview">
        <div>
          <PackageCard
            image={heroImage || '/logo.png'}
            title={tripInfo.title || 'Package title'}
            tag={rating ? `${rating} ★` : undefined}
            duration={`${nightsCount + 1}D/${nightsCount}N`}
            features={highlights}
            inclusions={tripInfo.inclusions}
            highlight={tripInfo.subtitle}
            pricePerPerson={Number(tripInfo.totalPrice || 0)}
          />

          {!!websitePreviewUrl && (
            <div className="package-editor-preview-link" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <a href={websitePreviewUrl} target="_blank" rel="noreferrer">Open full preview ↗</a>
              <button 
                type="button"
                onClick={() => setShowPDF(true)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: 'inherit', 
                  textDecoration: 'underline', 
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                View Quotation PDF ↗
              </button>
            </div>
          )}
        </div>
      </Section>

      {/* Package Summary section removed as requested */}

      <Section title="Commercials & Cost Breakdown">
        <div className="package-editor-breakdown-section">
          <div className="package-editor-breakdown-title">Auto-calculated Expenses</div>
          <div className="package-editor-breakdown-item"><span>Flights</span><span>{currencySymbol}{flightsTotal.toLocaleString('en-IN')}</span></div>
          <div className="package-editor-breakdown-item"><span>Hotels</span><span>{currencySymbol}{hotelsTotal.toLocaleString('en-IN')}</span></div>
          <div className="package-editor-breakdown-item"><span>Activities</span><span>{currencySymbol}{activitiesTotal.toLocaleString('en-IN')}</span></div>
          <div className="package-editor-breakdown-item"><span>Transfers</span><span>{currencySymbol}{transfersTotal.toLocaleString('en-IN')}</span></div>
          <div className="package-editor-breakdown-item"><span>Add-ons</span><span>{currencySymbol}{addonsTotal.toLocaleString('en-IN')}</span></div>
          <div className="package-editor-breakdown-total"><span>Total Auto-calculated</span><span>{currencySymbol}{autoTotal.toLocaleString('en-IN')}</span></div>
        </div>

        <div className="package-editor-box-light">
          <div className="package-editor-box-title">Manual Price Override</div>
          <div className="package-editor-box-row">
            <span>Total Price:</span>
            <input
              type="number"
              min={0}
              value={tripInfo.totalPrice}
              onChange={(e) => onChangeTotalPrice(Number(e.target.value || 0))}
              placeholder="0"
              className="package-editor-input package-editor-box-input"
            />
            <span>{currencySymbol}</span>
          </div>
          <div className="package-editor-help">Leave empty to use auto-calculated total</div>
        </div>

        <div className="package-editor-final-price">
          <div className="package-editor-final-price-title">Final Package Price</div>
          <div className="package-editor-final-price-value">
            {currencySymbol}
            {(tripInfo.totalPrice || autoTotal).toLocaleString('en-IN')}
          </div>
        </div>
      </Section>

      
    </div>
  );
};

export default PackageRightSidebar;
